import { StyledLink } from '@/components/escript/onboarding/Onboarding.styles';
import { usePreOnboardingContext } from '@/hooks/escript/usePreOnboardingContext';
import { paymentMethods } from '@/pages/escript/landing/PaymentMethods';
import { LOGIN_ROUTE, UMEDS_MARKETING } from '@/routes/route-paths';
import { ContainedButtonInverted, OutlinedButtonInverted } from '@/theme/buttons';
import UmedsLogo from '@montugroup/circuit-shared/components/common/UmedsLogo';
import { Box, Link, Stack, Typography } from '@montugroup/design-system';
import { MgFacebookAlt, MgInstagramAlt, MgLinkedInAlt } from '@montugroup/icons';
import { Grid2, styled } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  SocialIcons,
  StyledButtonStack,
  StyledGridItemFooter,
  StyledNav,
  StyledPaymentOptionsFooter,
  StyledUmedsLogoFooterSubText,
} from './OnboardingFooter.styles';

const LOGO_SIZE = '220px';
const StyledUmedsLogoFooter = styled(UmedsLogo)(() => ({
  width: LOGO_SIZE,
}));

export function OnboardingFooterDesktop() {
  const { resetOnboardingState } = usePreOnboardingContext();

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 6 }}>
        <StyledGridItemFooter>
          <Box>
            <Box sx={(theme) => ({ maxWidth: LOGO_SIZE, marginBottom: theme.spacing(20) })}>
              <StyledUmedsLogoFooter fillColor={(theme) => theme.palette.secondary.main}></StyledUmedsLogoFooter>
              <StyledUmedsLogoFooterSubText>In good hands</StyledUmedsLogoFooterSubText>
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={(theme) => ({ marginBottom: theme.spacing(4), color: theme.palette.common.white })}
              >
                &copy; {new Date().getFullYear()} uMeds Australia Pty Ltd. All rights reserved.
              </Typography>

              <Stack direction="row" gap={4} sx={(theme) => ({ marginBottom: theme.spacing(6) })}>
                {paymentMethods.map((item, index) => (
                  <StyledPaymentOptionsFooter key={`${item.name}-${index}-desktop`}>
                    <img src={item.src} alt={item.name} title={item.name} />
                  </StyledPaymentOptionsFooter>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={8}>
              <StyledLink
                href={UMEDS_MARKETING.PRIVACY_POLICY}
                data-tracking-id="escript_onboarding_footer_privacy_policy"
              >
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.common.white })}>
                  Privacy Policy
                </Typography>
              </StyledLink>

              <StyledLink
                href={UMEDS_MARKETING.TERMS_CONDITIONS}
                data-tracking-id="escript_onboarding_footer_terms_conditions"
              >
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.common.white })}>
                  Terms of Service
                </Typography>
              </StyledLink>
            </Stack>
          </Box>
        </StyledGridItemFooter>
      </Grid2>

      <Grid2 size={{ xs: 6 }} sx={{ justifyItems: 'flex-end' }}>
        <StyledGridItemFooter>
          <StyledButtonStack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 7, md: 4 }}>
            <OutlinedButtonInverted
              component={RouterLink}
              to={LOGIN_ROUTE}
              variant="outlined"
              color="primary"
              size="large"
              data-tracking-id="escript_onboarding_footer_login"
            >
              Sign In
            </OutlinedButtonInverted>

            <ContainedButtonInverted
              size="large"
              variant="contained"
              data-tracking-id="escript_onboarding_footer_prescription"
              onClick={resetOnboardingState}
            >
              Submit Prescription
            </ContainedButtonInverted>
          </StyledButtonStack>

          <StyledNav component="nav" aria-label="Footer Navigation" sx={{ justifyContent: 'flex-end' }}>
            <SocialIcons>
              <Link
                href="https://www.facebook.com/profile.php?id=61557205078310"
                data-tracking-id="escript_onboarding_footer_facebook"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <MgFacebookAlt />
              </Link>

              <Link
                href="http://linkedin.com/company/umeds-australia"
                data-tracking-id="escript_onboarding_footer_linkedin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <MgLinkedInAlt />
              </Link>

              <Link
                href="https://www.instagram.com/umedsau/"
                data-tracking-id="escript_onboarding_footer_instagram"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <MgInstagramAlt />
              </Link>
            </SocialIcons>
          </StyledNav>
        </StyledGridItemFooter>
      </Grid2>
    </Grid2>
  );
}
